export default class SingleBmaReview {
    constructor() {
        this.section = document.querySelector('.block-same-edition-news');
        if (!this.section) return;

        this.apiUrl = this.section.dataset.apiUrl;
        this.wrapper = this.section.querySelector('.same-edition-news-wrapper');
        this.sentinel = this.section.querySelector('.same-edition-sentinel');
        this.editionNews = this.parseEditionNews();
        this.contentNavigationLabel = this.section.dataset.contentNavigationLabel || 'Neste conteúdo';
        this.editionNewsLabel = this.section.dataset.editionNewsLabel || 'Nesta edição';

        this.allNews = [];
        this.currentIndex = 0;
        this.hasFetched = false;
        this.isLoading = false;

        this.originalUrl = window.location.href;
        this.activeUrl = this.originalUrl;

        this.initSentinelObserver();
        this.initUrlObserver();
    }

    initSentinelObserver() {
        this.sentinelObserver = new IntersectionObserver(
            (entries) => this.handleSentinel(entries),
            { rootMargin: '0px 0px 100% 0px' }
        );
        this.sentinelObserver.observe(this.sentinel);
    }

    initUrlObserver() {
        const originalSection = document.querySelector('.page-news-single[data-no-index]');
        if (originalSection) {
            originalSection.dataset.url = this.originalUrl;
        }

        this.urlObserver = new IntersectionObserver(
            (entries) => this.handleUrlChange(entries),
            { rootMargin: '-40% 0px -40% 0px' }
        );

        if (originalSection) {
            this.urlObserver.observe(originalSection);
        }
    }

    handleUrlChange(entries) {
        for (const entry of entries) {
            if (!entry.isIntersecting) continue;

            const url = entry.target.dataset.url;
            if (url && url !== this.activeUrl) {
                this.activeUrl = url;
                window.history.replaceState(null, '', url);
            }
        }
    }

    handleSentinel(entries) {
        for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            if (this.isLoading) return;

            if (!this.hasFetched) {
                this.isLoading = true;
                this.fetchNews().then(() => {
                    this.isLoading = false;
                    this.tryAppendNext();
                });
                return;
            }

            this.tryAppendNext();
        }
    }

    tryAppendNext() {
        if (this.currentIndex < this.allNews.length) {
            this.appendNextNews();
        }

        if (this.currentIndex >= this.allNews.length) {
            this.sentinelObserver.unobserve(this.sentinel);
        }
    }

    async fetchNews() {
        try {
            const response = await fetch(this.apiUrl);
            if (!response.ok) throw new Error('Failed to fetch');
            const data = await response.json();
            this.allNews = data.data || [];
            this.hasFetched = true;
        } catch (error) {
            console.error('Erro ao carregar notícias da mesma edição:', error);
            this.sentinelObserver.unobserve(this.sentinel);
        }
    }

    appendNextNews() {
        if (this.currentIndex >= this.allNews.length) return;

        const news = this.allNews[this.currentIndex];
        this.currentIndex++;

        const article = this.createArticleSection(news);
        this.wrapper.appendChild(article);
        window.initProfessionalsCarousels?.(article);
        this.urlObserver.observe(article);
    }

    createArticleSection(news) {
        const section = document.createElement('article');
        section.className = 'page-news-single relative block w-full';
        section.setAttribute('data-no-index', '');
        section.dataset.url = news.url;

        const title = this.escapeHtml(news.title);
        const date = this.escapeHtml(news.date || '');
        const readingTime = this.escapeHtml(news.formatted_reading_time || '');
        const editionName = news.edition ? this.escapeHtml(news.edition.name) : '';
        const categoryName = news.categories && news.categories.length > 0
            ? this.escapeHtml(news.categories[0].name)
            : 'BMA Review';
        const categoryUrl = news.categories && news.categories.length > 0
            ? this.escapeHtml(news.categories[0].url)
            : '';
        const imgSrc = news.image_internal;

        let html = '';

        // === HEADER BLOCK ===
        html += `<div class="news-print-heading block relative bg-bege dark:bg-darkmode">
            <div class="container pt-14">
                <div class="mb-4 flex justify-center gap-2">`;

        if (news.categories) {
            news.categories.forEach(cat => {
                html += `<span class="inline-block bg-white dark:bg-darkmode px-3 py-[5px] text-xs font-bold uppercase text-cinza dark:text-darkmode-muted">${this.escapeHtml(cat.name)}</span>`;
            });
        }

        html += `</div>
                <h1 class="mb-4 text-center text-2xl/130 font-bold text-black dark:text-darkmode-text lg:text-40/120">${title}</h1>
                <div class="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 ${imgSrc ? 'pb-8' : 'pb-12'} text-sm/150 font-bold text-secondary dark:text-white">
                    ${editionName ? `<span class="w-full text-center lg:w-auto">${editionName}</span>` : ''}
                    <span>${date}</span>
                    ${readingTime ? `<span>${readingTime}</span>` : ''}
                </div>
            </div>`;

        if (imgSrc) {
            html += `<div class="relative block w-full">
                <div class="container relative z-5">
                    <picture class="relative block h-[300px] w-full overflow-hidden lg:h-[510px]">
                        <img src="${this.escapeHtml(imgSrc)}" alt="${title}" class="block h-full w-full object-cover dark:grayscale" loading="lazy" />
                    </picture>
                </div>
                <div class="absolute bottom-0 left-0 z-1 h-1/2 w-full bg-offwhite dark:bg-darkmode-surface"></div>
            </div>`;
        }

        html += `<div class="absolute ${imgSrc ? 'lg:bottom-[31%] bottom-[26%]' : 'bottom-0'} w-full flex-col justify-end flex z-1">
            <div class="relative z-1 block w-full lg:h-[10px] h-[7px] bg-redwine2 dark:bg-darkmode-border"></div>
            <div class="relative z-1 block w-full lg:h-[20px] h-[10px] bg-redwine1 dark:bg-darkmode-surface"></div>
        </div>`;

        html += `</div>`;

        // === CONTENT + SIDEBAR ===
        html += `<div class="container">
            <div class="flex w-full flex-col py-10 lg:flex-row lg:gap-8 lg:py-14">
                <aside class="contents lg:sticky lg:top-36 lg:flex lg:h-fit lg:w-[336px] lg:flex-shrink-0 lg:flex-col lg:self-start">`;

        const authorsAndProfessionals = this.mergeAuthorsAndProfessionals(news);
        if (authorsAndProfessionals.length > 0) {
            html += `<div class="flex flex-col gap-5">
                        <p class="text-sm/150 font-bold mb-2">Autores</p>
                        <div class="flex flex-col gap-4">`;
            authorsAndProfessionals.forEach(item => {
                html += `<p class="text-sm/150 font-semibold">${this.escapeHtml(item.name)}</p>`;
            });
            html += `</div>
                    </div>
                    <div class="block w-full h-px bg-cinza-claro2 dark:bg-darkmode-subtle my-8"></div>`;
        }

        html += `<nav class="article-table-of-contents hidden max-lg:!hidden" aria-label="${this.escapeHtml(this.contentNavigationLabel)}">
                    <p class="mb-4 text-sm/150 font-bold">${this.escapeHtml(this.contentNavigationLabel)}</p>
                    <ol class="article-table-of-contents-list flex flex-col gap-3"></ol>
                    <div class="my-8 block h-px w-full bg-cinza-claro2 dark:bg-darkmode-subtle"></div>
                </nav>`;

        html += this.createEditionNewsNavigation(news.id);

        if (news.expertises_areas && news.expertises_areas.length > 0) {
            html += `<div class="flex flex-col gap-4">
                        <p class="text-sm/150 font-bold">Áreas relacionadas</p>
                        <div class="flex flex-row flex-wrap gap-2">`;
            news.expertises_areas.forEach(area => {
                html += `<span class="bg-cinza dark:bg-darkmode-subtle border border-cinza dark:border-darkmode-border text-white px-2 py-1 text-xs/150 font-bold">${this.escapeHtml(area.title)}</span>`;
            });
            html += `</div>
                    </div>
                    <div class="block w-full h-px bg-cinza-claro2 dark:bg-darkmode-subtle my-8"></div>`;
        }

        html += `<div class="order-3 mt-[60px] flex flex-col lg:contents lg:mt-0">`;

        if (news.tags && news.tags.length > 0) {
            html += `<div class="flex flex-col gap-4">
                        <p class="text-sm/150 font-bold">Assuntos</p>
                        <div class="flex flex-row flex-wrap gap-2">`;
            news.tags.forEach(tag => {
                html += `<span class="border border-cinza dark:border-darkmode-border text-cinza dark:text-darkmode-muted px-2 py-1 text-xs/150 font-bold">${this.escapeHtml(tag.name)}</span>`;
            });
            html += `</div>
                    </div>
                    <div class="block w-full h-px bg-cinza-claro2 dark:bg-darkmode-subtle my-8"></div>`;
        }

        const shareUrl = encodeURIComponent(news.url);
        const shareTitle = encodeURIComponent(news.title);
        const copyUrl = new URL(news.url, window.location.origin);
        html += `<div class="news-print-actions flex flex-row gap-6 items-center">
                        <p class="text-sm/150 font-bold">Compartilhe</p>
                        <ul class="flex flex-wrap gap-2">
                            <li class="relative">
                                <button class="btnCopyDynamicLink flex h-8 w-8 text-darkest dark:text-white items-center justify-center hover:text-secondary transition-all" type="button" aria-label="Copiar link" data-copy-url="${this.escapeHtml(copyUrl.toString())}">
                                    <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M1.46195 17.018C1.92583 17.4825 2.47692 17.8508 3.08357 18.1017C3.69022 18.3526 4.34047 18.4811 4.99695 18.48C5.65358 18.4811 6.30397 18.3525 6.91078 18.1016C7.51759 17.8508 8.06886 17.4825 8.53295 17.018L11.361 14.189L9.94695 12.775L7.11895 15.604C6.55542 16.165 5.79262 16.4799 4.99745 16.4799C4.20228 16.4799 3.43948 16.165 2.87595 15.604C2.31446 15.0407 1.99916 14.2778 1.99916 13.4825C1.99916 12.6871 2.31446 11.9242 2.87595 11.361L5.70495 8.53295L4.29095 7.11895L1.46195 9.94695C0.525758 10.8854 0 12.1569 0 13.4825C0 14.808 0.525758 16.0795 1.46195 17.018ZM17.018 8.53295C17.9537 7.59424 18.4791 6.32287 18.4791 4.99745C18.4791 3.67203 17.9537 2.40067 17.018 1.46195C16.0795 0.525758 14.808 0 13.4825 0C12.1569 0 10.8854 0.525758 9.94695 1.46195L7.11895 4.29095L8.53295 5.70495L11.361 2.87595C11.9245 2.31494 12.6873 1.99998 13.4825 1.99998C14.2776 1.99998 15.0404 2.31494 15.604 2.87595C16.1654 3.43922 16.4807 4.20212 16.4807 4.99745C16.4807 5.79278 16.1654 6.55568 15.604 7.11895L12.775 9.94695L14.189 11.361L17.018 8.53295Z" fill="currentColor"/>
                                        <path d="M5.70394 14.19L4.28894 12.776L12.7759 4.29004L14.1899 5.70504L5.70394 14.19Z" fill="currentColor"/>
                                    </svg>
                                </button>
                                <span class="labelCopyUrl absolute -bottom-12 left-0 z-50 hidden w-max rounded-lg bg-darkest p-2 text-xs text-white">URL Copiada para a área de transferência</span>
                            </li>
                            <li>
                                <a class="flex h-8 w-8 text-darkest dark:text-white items-center justify-center hover:text-secondary transition-all" href="https://wa.me/?text=${shareTitle}%20${shareUrl}" aria-label="Compartilhar no WhatsApp" target="_blank">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                                </a>
                            </li>
                            <li>
                                <a class="flex h-8 w-8 text-darkest dark:text-white items-center justify-center hover:text-secondary transition-all" href="https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}" aria-label="Compartilhar no LinkedIn" target="_blank" rel="noopener noreferrer">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                                </a>
                            </li>
                            <li>
                                <a class="flex h-8 w-8 text-darkest dark:text-white items-center justify-center hover:text-secondary transition-all" href="https://twitter.com/intent/tweet?text=${shareTitle}&url=${shareUrl}" aria-label="Compartilhar no X" target="_blank">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                                </a>
                            </li>
                            <li>
                                <button class="flex h-8 w-8 text-darkest dark:text-white items-center justify-center hover:text-secondary transition-all" type="button" aria-label="Imprimir">
                                    <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M13.7513 13.7513H15.5846C16.0709 13.7513 16.5372 13.5581 16.881 13.2143C17.2248 12.8705 17.418 12.4042 17.418 11.918V8.2513C17.418 7.76507 17.2248 7.29876 16.881 6.95494C16.5372 6.61112 16.0709 6.41797 15.5846 6.41797H2.7513C2.26507 6.41797 1.79876 6.61112 1.45494 6.95494C1.11112 7.29876 0.917969 7.76507 0.917969 8.2513V11.918C0.917969 12.4042 1.11112 12.8705 1.45494 13.2143C1.79876 13.5581 2.26507 13.7513 2.7513 13.7513H4.58464M13.7513 6.41797V2.7513C13.7513 2.26507 13.5581 1.79876 13.2143 1.45494C12.8705 1.11112 12.4042 0.917969 11.918 0.917969H6.41797C5.93174 0.917969 5.46542 1.11112 5.12161 1.45494C4.77779 1.79876 4.58464 2.26507 4.58464 2.7513V6.41797M6.41797 10.0846H11.918C12.9305 10.0846 13.7513 10.9054 13.7513 11.918V15.5846C13.7513 16.5972 12.9305 17.418 11.918 17.418H6.41797C5.40545 17.418 4.58464 16.5972 4.58464 15.5846V11.918C4.58464 10.9054 5.40545 10.0846 6.41797 10.0846Z" stroke="currentColor" stroke-width="1.83333" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>
                </aside>
                <div class="order-2 flex min-w-0 flex-1 flex-col lg:order-none">
                    <div class="contentnews prose max-w-full dark:prose-invert dark:text-white">${news.content || news.abstract || ''}</div>
                </div>
            </div>
        </div>`;

        if (news.professionals_html) {
            html += news.professionals_html;
        }

        section.innerHTML = html;
        this.bindArticleActions(section);
        if (window.ArticleTableOfContents) {
            new window.ArticleTableOfContents(section);
        }
        return section;
    }

    bindArticleActions(section) {
        const copyButton = section.querySelector('.btnCopyDynamicLink');
        const printButton = section.querySelector('[aria-label="Imprimir"]');

        copyButton?.addEventListener('click', async () => {
            try {
                await navigator.clipboard.writeText(copyButton.dataset.copyUrl);
                const label = copyButton.nextElementSibling;
                label?.classList.remove('hidden');
                window.setTimeout(() => label?.classList.add('hidden'), 5000);
            } catch (error) {
                console.error('Erro ao copiar URL:', error);
            }
        });

        printButton?.addEventListener('click', () => window.print());
    }

    mergeAuthorsAndProfessionals(news) {
        const items = [];
        if (news.authors) {
            news.authors.forEach(a => items.push({ name: a.name }));
        }
        if (news.professionals) {
            news.professionals.forEach(p => items.push({ name: p.name }));
        }
        items.sort((a, b) => a.name.localeCompare(b.name));
        return items;
    }

    parseEditionNews() {
        const data = this.section.querySelector('.review-edition-news-data')?.textContent;

        if (!data) return [];

        try {
            return JSON.parse(data);
        } catch (error) {
            console.error('Erro ao carregar a lista de conteúdos da edição:', error);
            return [];
        }
    }

    createEditionNewsNavigation(currentNewsId) {
        const otherNews = this.editionNews.filter(news => String(news.id) !== String(currentNewsId));

        if (!otherNews.length) return '';

        const items = otherNews.map(news => `
            <li>
                <a href="${this.escapeHtml(news.url)}" class="block border-l-2 border-transparent pl-3 text-sm/150 text-cinza transition-colors hover:border-secondary hover:text-secondary dark:text-darkmode-muted dark:hover:text-white">
                    ${this.escapeHtml(news.title)}
                </a>
            </li>`).join('');

        return `<nav class="hidden lg:block" aria-label="${this.escapeHtml(this.editionNewsLabel)}">
                    <p class="mb-4 text-sm/150 font-bold">${this.escapeHtml(this.editionNewsLabel)}</p>
                    <ol class="flex max-h-[300px] flex-col gap-3 overflow-y-auto pr-2">${items}</ol>
                    <div class="my-8 block h-px w-full bg-cinza-claro2 dark:bg-darkmode-subtle"></div>
                </nav>`;
    }

    escapeHtml(str) {
        if (!str) return '';
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
    }
}

if (document.querySelector('.block-same-edition-news')) {
    new SingleBmaReview();
}
